import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { TrendingUp, TrendingDown, Activity, Target } from 'lucide-react';
import { format } from 'date-fns';

interface TradeEvent {
  id: string;
  timestamp: string;
  instrument: string;
  action: string;
  amount: number | null;
  win: boolean | null;
  bot_name: string;
}

interface TradeStats {
  totalTrades: number;
  wins: number;
  losses: number;
  winRate: number;
}

interface TradesHistoryTabProps {
  filters: {
    botId?: string;
    instrument?: string;
    dateFrom: string;
    dateTo: string;
  };
  refreshKey: number;
  bots: { id: string; name: string; signal_token: string }[];
}

export function TradesHistoryTab({ filters, refreshKey, bots }: TradesHistoryTabProps) {
  const [trades, setTrades] = useState<TradeEvent[]>([]);
  const [stats, setStats] = useState<TradeStats>({
    totalTrades: 0,
    wins: 0,
    losses: 0,
    winRate: 0,
  });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    fetchTradesHistory();
  }, [filters, refreshKey]);

  const fetchTradesHistory = async () => {
    setIsLoading(true);
    try {
      let query = supabase
        .from('bot_signal_events')
        .select(`
          id,
          timestamp,
          instrument,
          action,
          amount,
          win,
          bots!bot_id(name)
        `)
        .eq('action_type', 'EXIT')
        .gte('timestamp', `${filters.dateFrom}T00:00:00Z`)
        .lte('timestamp', `${filters.dateTo}T23:59:59Z`)
        .order('timestamp', { ascending: false });

      if (filters.botId) {
        query = query.eq('bot_id', filters.botId);
      }

      if (filters.instrument) {
        query = query.eq('instrument', filters.instrument);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching trades history:', error);
        return;
      }

      const tradesData = (data || []).map(trade => ({
        id: trade.id,
        timestamp: trade.timestamp,
        instrument: trade.instrument,
        action: trade.action,
        amount: trade.amount,
        win: trade.win,
        bot_name: (trade as any).bots?.name || 'Unknown Bot',
      }));

      setTrades(tradesData);

      // Calculate stats
      const validTrades = tradesData.filter(trade => trade.win !== null);
      const wins = validTrades.filter(trade => trade.win === true).length;
      const losses = validTrades.filter(trade => trade.win === false).length;
      const winRate = validTrades.length > 0 ? (wins / validTrades.length) * 100 : 0;

      setStats({
        totalTrades: validTrades.length,
        wins,
        losses,
        winRate,
      });

    } catch (error) {
      console.error('Error fetching trades history:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getActionBadge = (action: string) => {
    const isLong = action.includes('LONG');
    const variant = isLong ? 'default' : 'secondary';
    const icon = isLong ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />;
    
    return (
      <Badge variant={variant} className="flex items-center gap-1">
        {icon}
        {action}
      </Badge>
    );
  };

  const getWinBadge = (win: boolean | null) => {
    if (win === null) return <Badge variant="outline">N/A</Badge>;
    return (
      <Badge variant={win ? 'default' : 'destructive'}>
        {win ? 'WIN' : 'LOSS'}
      </Badge>
    );
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Trades</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalTrades}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Wins</CardTitle>
            <TrendingUp className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{stats.wins}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Losses</CardTitle>
            <TrendingDown className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{stats.losses}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Win Rate</CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.winRate.toFixed(1)}%</div>
          </CardContent>
        </Card>
      </div>

      {/* Trades Table */}
      <Card>
        <CardHeader>
          <CardTitle>Exit Trades History</CardTitle>
          <CardDescription>
            Shows only EXIT signals with WIN/LOSS results
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-sm text-muted-foreground">Loading trades...</div>
            </div>
          ) : trades.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-sm text-muted-foreground">No trades found for the selected filters</div>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Timestamp</TableHead>
                  <TableHead>Bot</TableHead>
                  <TableHead>Instrument</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Result</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {trades.map((trade) => (
                  <TableRow key={trade.id}>
                    <TableCell className="font-mono text-sm">
                      {format(new Date(trade.timestamp), 'yyyy-MM-dd HH:mm:ss')}
                    </TableCell>
                    <TableCell className="font-medium">
                      {trade.bot_name}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{trade.instrument}</Badge>
                    </TableCell>
                    <TableCell>
                      {getActionBadge(trade.action)}
                    </TableCell>
                    <TableCell>
                      {trade.amount ? trade.amount.toString() : 'N/A'}
                    </TableCell>
                    <TableCell>
                      {getWinBadge(trade.win)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}